import { CascadeOption, Option } from '@/components/form/inputs/CascadeSelect';
import { HotCitiesType } from '@/components/city/CityCard';

export interface LocationDataValue {
  id: string;
  name: string;
  pinyin: string;
  house_price: number;
}

export interface JobTitleLocationDataValue {
  name: string;
  pinyin: string;
  house_price: number;
  job_open_count: number;
  avg: number;
  competitive_rate: number;
  competitive_rank: number;
  avg_median?: number | null;
}

export interface JobTitleSummaryValue {
  new_job_details: {
    by_month: { name: string; value: any; growth_rate?: number }[];
  };
}

export type JobOpenDetailsType = {
  current_count: number;
  open_job_details: {
    by_year: { name: string | number; value: any }[];
    by_month: { name: string; value: any }[];
    by_experience?: {
      by_year?: { name: string; value: any }[];
    };
  };
  new_job_details: {
    by_month: {
      name: string;
      value: number;
      rank: number;
      percent_rank: number;
      growth_rate?: number;
      growth_rate_rank?: number;
    }[];
    by_experience: {
      by_month: { name: string; value: any }[];
    };
    salary_max_of_all_period: number;
    salary_min_of_all_period: number;
    last_sorted_month_by_experience: { name: string; value: any }[];
    last_month_by_experience: { name: string; value: any }[];
  };
  avg_new_job_details: {
    monthly: {
      count: number;
      rank: number;
      by_experience: { name: string; value: any }[];
    };
  };
  updated_at: string;
};

export type JobSalaryDetailsType = {
  max: number;
  min: number;
  avg: number;
  avg_median?: number | null;
  median_avg?: number | null;
  min_median?: number | null;
  max_median?: number | null;
  competitive_rate: number;
  competitive_rank: number;
  avg_rank_details: {
    by_location: number;
    by_job_title: number;
  };
  avg_growth_rate_details: {
    by_experience: {
      value?: number;
      rank?: number;
    };
  };
  avg_salary_details: {
    by_year: {
      name: string | number;
      value: any;
      growth_rate?: number;
      growth_rate_rank?: number;
    }[];
    by_month: {
      name: string;
      value: number;
    }[];
  };
  avg_value_by_education_details: Record<string, Record<string, number>>;
  avg_value_by_experience_details: Record<string, Record<string, number>>;
  distribution_details: {
    [key: string]: number;
  };
};

export interface JobTitleDataValue {
  id: string;
  name: string;
  display_name: string;
  city_name?: string;
  aliases: Option[];
  province_city_data: CascadeOption[];
  l1_l2_list: Option[];
  description: string;
  job_difficulty_rating: number;
  job_challenge_rating: number;
  job_future_rating: number;
  job_open_details: JobOpenDetailsType;
  salary_details: JobSalaryDetailsType;
  salary_rank: {
    current_rank: number;
    l1_id: string;
    l1_name: string;
    ranks: { id: string; name: string; avg: number }[];
  };
  locations: JobTitleLocationDataValue[];
  location?: LocationDataValue;
  skill_details: {
    required_skills: {
      name: string;
      type: string;
    }[];
    required_by_education: {
      [key: string]: string[];
    };
    required_by_experience: {
      [key: string]: string[];
    };
  };
  current_count_group_by_experience: { name: string; value: number }[];
  location_salary?: HotCitiesType;
}

export interface SimilarJobTitleType {
  id: string;
  name: string;
  salary: number;
  description: string;
}

export interface MoreJobTitleType {
  page_path: string;
  page_data: {
    id: string;
    salary: number;
    city_pinyin?: string;
    city_name?: string;
    name: string;
    description: string;
  };
}

export interface JobTitleContextValue {
  jobTitle: JobTitleDataValue;
  jobTitleSummary: JobTitleSummaryValue;
  location?: LocationDataValue;
  displayName: string;
}

export const defaultJobTitleData = {
  id: '',
  name: '',
  display_name: '',
  display_city: '',
  aliases: [],
  province_city_data: [],
  l1_l2_list: [],
  description: '',
  job_difficulty_rating: NaN,
  job_challenge_rating: NaN,
  job_future_rating: NaN,
  job_open_details: {
    current_count: NaN,
    open_job_details: { by_year: [], by_month: [] },
    new_job_details: {
      by_year: [],
      by_month: [],
      by_experience: {
        by_month: [],
      },
      salary_max_of_all_period: NaN,
      salary_min_of_all_period: NaN,
      last_sorted_month_by_experience: [{ name: '', value: [] }],
      last_month_by_experience: [{ name: '', value: [] }],
    },
    avg_new_job_details: {
      monthly: {
        count: NaN,
        rank: NaN,
        by_experience: [],
      },
    },
    updated_at: '',
  },
  salary_details: {
    max: NaN,
    min: NaN,
    avg: NaN,
    competitive_rank: NaN,
    competitive_rate: NaN,
    avg_rank_details: {
      by_location: NaN,
      by_job_title: NaN,
    },
    avg_growth_rate_details: {
      by_experience: {
        value: NaN,
        rank: NaN,
      },
    },
    avg_salary_details: {
      by_year: [],
      by_month: [],
    },
    avg_value_by_education_details: {},
    avg_value_by_experience_details: {},
    distribution_details: {},
  },
  salary_rank: { l1_id: '', l1_name: '', current_rank: NaN, ranks: [] },
  locations: [],
  skill_details: {
    required_skills: [],
    required_by_education: {},
    required_by_experience: {},
  },
  current_count_group_by_experience: [],
  hot_jobs: [],
  location_salary: undefined,
};

export const defaultJobTitleContext = {
  jobTitle: defaultJobTitleData,
  jobTitleSummary: { new_job_details: { by_month: [] } },
  displayName: '',
};
